import React, { useEffect, useState, useCallback } from 'react';

export default function AudioPrompt({ text, autoPlay = true, onPlayComplete }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSupported, setIsSupported] = useState(true);

  const speak = useCallback(() => {
    if (!window.speechSynthesis || !text) {
      setIsSupported(false);
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9; // Slightly slower for young students
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => {
      setIsPlaying(false);
      onPlayComplete?.();
    };
    utterance.onerror = () => {
      setIsPlaying(false);
      setIsSupported(false);
    };

    window.speechSynthesis.speak(utterance);
  }, [text, onPlayComplete]);

  useEffect(() => {
    // Check for speech synthesis support
    if (!window.speechSynthesis) {
      setIsSupported(false);
      return;
    }

    // Auto-play on mount if enabled and user has interacted with page
    if (autoPlay) {
      // Small delay to ensure voices are loaded
      const timer = setTimeout(() => {
        speak();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoPlay, speak]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  if (!isSupported) {
    // Fallback: show text prompt for older students or when TTS unavailable
    return (
      <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-lg font-medium text-gray-900">{text}</p>
      </div>
    );
  }

  return (
    <div className="mb-4 flex items-center space-x-3">
      <button
        type="button"
        onClick={speak}
        disabled={isPlaying}
        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
          isPlaying
            ? 'bg-indigo-100 text-indigo-700 cursor-wait'
            : 'bg-indigo-600 text-white hover:bg-indigo-700'
        }`}
        aria-label={isPlaying ? 'Playing audio' : 'Play audio prompt'}
      >
        {isPlaying ? (
          <>
            <svg className="animate-pulse -ml-1 mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
            Playing...
          </>
        ) : (
          <>
            <svg className="-ml-1 mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
            Listen Again
          </>
        )}
      </button>
      <span className="text-sm text-gray-500">
        {isPlaying ? 'Tap to replay' : 'Tap to hear the question'}
      </span>
    </div>
  );
}
